import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import Header from '../../components/common/Header';
import Sidebar from '../../components/common/Sidebar';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import {
  Users, Building2, Layers, FileText, Bot, ArrowRight, Shield
} from 'lucide-react';
import './AdminPages.css';

const AdminDashboard = () => {
  const [stats, setStats] = useState(null);
  const [aiAnalytics, setAiAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);

  const navigate = useNavigate();

  useEffect(() => {
    fetchAdminOverview();
  }, []);

  const fetchAdminOverview = async () => {
    try {
      setLoading(true);
      const [empRes, deptRes, projRes, polRes, aiRes] = await Promise.all([
        api.get('/employees/'),
        api.get('/departments/'),
        api.get('/projects/'),
        api.get('/company-policies/'),
        api.get('/admin/ai-analytics/')
      ]);

      const empList = empRes.data.results || empRes.data;
      const deptList = deptRes.data.results || deptRes.data;
      const projList = projRes.data.results || projRes.data;
      const polList = polRes.data.results || polRes.data;

      setStats({
        totalEmployees: empList.length,
        activeEmployees: empList.filter(e => e.is_active).length,
        departmentsCount: deptList.length,
        projectsCount: projList.length,
        policiesCount: polList.length
      });

      setAiAnalytics(aiRes.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="page-layout-root">
      <Header />
      <div className="page-body-wrapper">
        <Sidebar />
        <main className="page-main-content">
          
          <div className="admin-page-header">
            <div>
              <div className="badge badge-primary" style={{ marginBottom: '8px' }}>
                <Shield size={12} /> Super Admin Control Center
              </div>
              <h1 className="admin-page-title">MentorAI Platform Overview</h1>
              <p className="admin-page-subtitle">Manage enterprise onboarding, departments, project documentation, and policy knowledge base.</p>
            </div>
          </div>

          {loading ? <LoadingSpinner label="Loading admin metrics..." /> : (
            <>
              {/* Stat Cards Grid */}
              <div className="admin-grid-4col" style={{ marginBottom: '32px' }}>
                
                <div className="card" style={{ padding: '24px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
                    <span className="admin-stat-label">Total Employees</span>
                    <Users size={22} style={{ color: '#4f46e5' }} />
                  </div>
                  <div className="admin-stat-value">{stats?.totalEmployees || 0}</div>
                  <span className="badge badge-success" style={{ marginTop: '8px' }}>{stats?.activeEmployees || 0} Active</span>
                </div>

                <div className="card" style={{ padding: '24px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
                    <span className="admin-stat-label">Departments</span>
                    <Building2 size={22} style={{ color: '#059669' }} />
                  </div>
                  <div className="admin-stat-value">{stats?.departmentsCount || 0}</div>
                  <span style={{ fontSize: '0.75rem', color: '#64748b', marginTop: '8px', display: 'block' }}>Configured Units</span>
                </div>

                <div className="card" style={{ padding: '24px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
                    <span className="admin-stat-label">Projects</span>
                    <Layers size={22} style={{ color: '#0284c7' }} />
                  </div>
                  <div className="admin-stat-value">{stats?.projectsCount || 0}</div>
                  <span style={{ fontSize: '0.75rem', color: '#64748b', marginTop: '8px', display: 'block' }}>Repositories</span>
                </div>

                <div className="card" style={{ padding: '24px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
                    <span className="admin-stat-label">Company Policies</span>
                    <FileText size={22} style={{ color: '#9333ea' }} />
                  </div>
                  <div className="admin-stat-value">{stats?.policiesCount || 0}</div>
                  <span style={{ fontSize: '0.75rem', color: '#64748b', marginTop: '8px', display: 'block' }}>RAG Indexed Docs</span>
                </div>

              </div>

              {/* AI Analytics & Recent Queries */}
              <div className="admin-grid-2col">
                
                <div className="card" style={{ padding: '28px' }}>
                  <h2 style={{ fontSize: '1.1rem', fontWeight: 700, marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Bot size={20} style={{ color: '#4f46e5' }} /> AI RAG Mentoring Analytics
                  </h2>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '20px' }}>
                    <div style={{ padding: '16px', background: 'rgba(15, 23, 42, 0.8)', borderRadius: '14px', border: '1px solid rgba(255, 255, 255, 0.08)' }}>
                      <div style={{ fontSize: '0.75rem', color: '#94a3b8', fontWeight: 600 }}>Total AI Questions</div>
                      <div style={{ fontSize: '1.6rem', fontWeight: 800, color: '#818cf8', marginTop: '4px' }}>{aiAnalytics?.total_user_questions || 0}</div>
                    </div>
                    <div style={{ padding: '16px', background: 'rgba(15, 23, 42, 0.8)', borderRadius: '14px', border: '1px solid rgba(255, 255, 255, 0.08)' }}>
                      <div style={{ fontSize: '0.75rem', color: '#94a3b8', fontWeight: 600 }}>Active Chat Sessions</div>
                      <div style={{ fontSize: '1.6rem', fontWeight: 800, color: '#34d399', marginTop: '4px' }}>
                        {(aiAnalytics?.policy_chat_sessions || 0) + (aiAnalytics?.project_chat_sessions || 0)}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="card" style={{ padding: '28px' }}>
                  <h2 style={{ fontSize: '1.1rem', fontWeight: 700, marginBottom: '16px' }}>Quick Admin Actions</h2>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    <button className="btn btn-secondary" onClick={() => navigate('/admin/employees')} style={{ justifyContent: 'space-between' }}>
                      <span>Create & Manage Employees</span> <ArrowRight size={16} />
                    </button>
                    <button className="btn btn-secondary" onClick={() => navigate('/admin/projects')} style={{ justifyContent: 'space-between' }}>
                      <span>Upload Project Documents & Workflow</span> <ArrowRight size={16} />
                    </button>
                    <button className="btn btn-secondary" onClick={() => navigate('/admin/policies')} style={{ justifyContent: 'space-between' }}>
                      <span>Upload Policy Documents & Manage FAQs</span> <ArrowRight size={16} />
                    </button>
                  </div>
                </div>

              </div>

            </>
          )}

        </main>
      </div>
    </div>
  );
};

export default AdminDashboard;
